import * as Sentry from '@sentry/react'
import { Component, ErrorInfo, ReactNode } from 'react'
import { track, eventTypes, IUser } from 'torqcloud-ui'

type ErrorBoundaryFallbackRender = (args: { error: Error; reset: () => void }) => ReactNode

interface ErrorBoundaryProps {
  children: ReactNode
  // Identifies this boundary in the APP_ERROR telemetry so we can tell which part
  // of the app failed (e.g. 'app' for the top-level boundary, 'route' for the
  // per-page one). Also used to build the fallback element ids. Defaults to 'app'.
  boundaryName?: string
  // 'fullscreen' fills the viewport, for a top-level crash where the whole app is
  // unusable. 'inline' fills its container instead, so a single subtree can fail
  // without blanking the surrounding chrome (header, sidebar, etc.).
  variant?: 'fullscreen' | 'inline'
  // Custom fallback UI. Either a node, or a render function that receives the
  // caught error and a reset() to clear the boundary and re-render children.
  // When omitted, a default fallback (matching `variant`) is shown.
  fallback?: ReactNode | ErrorBoundaryFallbackRender
  // When any value here changes, a boundary that is currently showing its
  // fallback auto-resets. Pass e.g. the route path so navigating away from a
  // crashed page recovers automatically instead of stranding the user.
  resetKeys?: unknown[]
  currentUser?: IUser
}

interface ErrorBoundaryState {
  error: Error | null
}

// Scoped, self-contained styles for the fallback. The top-level boundary sits
// above the MUI ThemeProvider (see index.tsx), so it renders without theme, `sx`,
// or any app context that the caught error may have left in a broken state.
// Everything the fallback needs (brand tokens, hover/focus/motion) is inlined
// here and namespaced under the boundary's own root id, so it cannot leak into
// the app or into another boundary's fallback rendering at the same time.
const fallbackStyles = (rootId: string, isInline: boolean): string => `
#${rootId} {
  box-sizing: border-box;
  ${
    isInline
      ? `position: relative;
  width: 100%;
  height: 100%;
  min-height: 320px;
  border-radius: 12px;`
      : `position: fixed;
  inset: 0;`
  }
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: #141B22;
  background-image:
    radial-gradient(120% 80% at 50% -10%, rgba(252, 212, 5, 0.06) 0%, rgba(252, 212, 5, 0) 55%),
    radial-gradient(100% 70% at 50% 120%, rgba(53, 66, 86, 0.5) 0%, rgba(31, 41, 52, 0) 60%);
  font-family: "Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  color: #ffffff;
  -webkit-font-smoothing: antialiased;
}
#${rootId} * { box-sizing: border-box; }
#${rootId} .eb-card {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: eb-rise 480ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
#${rootId} .eb-mark {
  position: relative;
  width: 64px;
  height: 64px;
  margin-bottom: 28px;
}
#${rootId} .eb-mark::after {
  content: "";
  position: absolute;
  inset: -34%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(252, 212, 5, 0.18) 0%, rgba(252, 212, 5, 0) 65%);
  z-index: -1;
  animation: eb-glow 4.5s ease-in-out infinite;
}
#${rootId} .eb-mark img {
  width: 64px;
  height: 64px;
  display: block;
  border-radius: 14px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45);
}
#${rootId} .eb-title {
  margin: 0 0 12px;
  font-size: 1.6rem;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #ffffff;
  text-wrap: balance;
}
#${rootId} .eb-body {
  margin: 0 0 32px;
  font-size: 1rem;
  line-height: 1.6;
  font-weight: 400;
  color: #A7B2C0;
  max-width: 38ch;
  text-wrap: pretty;
}
#${rootId} .eb-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: center;
}
#${rootId} .eb-btn {
  appearance: none;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1;
  border-radius: 10px;
  padding: 12px 22px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease,
    transform 160ms cubic-bezier(0.16, 1, 0.3, 1);
}
#${rootId} .eb-btn:focus-visible {
  outline: 2px solid #FCD405;
  outline-offset: 2px;
}
#${rootId} .eb-btn-primary { background-color: #FCD405; color: #141B22; }
#${rootId} .eb-btn-primary:hover { background-color: #FFE04D; transform: translateY(-1px); }
#${rootId} .eb-btn-primary:active { background-color: #E6C000; transform: translateY(0); }
#${rootId} .eb-btn-secondary {
  background-color: transparent;
  color: #C7D0DB;
  border-color: rgba(255, 255, 255, 0.14);
}
#${rootId} .eb-btn-secondary:hover {
  background-color: rgba(255, 255, 255, 0.06);
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.24);
}
#${rootId} .eb-btn-secondary:active { background-color: rgba(255, 255, 255, 0.03); }
@keyframes eb-rise {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes eb-glow {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
}
@media (max-width: 480px) {
  #${rootId} .eb-title { font-size: 1.4rem; }
  #${rootId} .eb-actions { flex-direction: column; align-self: stretch; }
  #${rootId} .eb-btn { width: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  #${rootId} .eb-card { animation: none; }
  #${rootId} .eb-mark::after { animation: none; }
  #${rootId} .eb-btn:hover { transform: none; }
}
`

// Reusable error boundary. Catches render/lifecycle errors in its subtree,
// reports them to Segment via the analytics wrapper (tagged with `boundaryName`),
// and shows a branded recovery UI. Mount it at the app root for a fullscreen
// catch-all and around individual route/feature subtrees for localized,
// recoverable fallbacks. Kept as a class because componentDidCatch has no hook
// equivalent.
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const boundary = this.props.boundaryName ?? 'app'
    const path = window.location.pathname
    Sentry.withScope(scope => {
      scope.setTag('boundary', boundary)
      scope.setTag('path', path)
      scope.setLevel('error')
      scope.setExtras({
        componentStack: errorInfo.componentStack,
      })
      if (this.props.currentUser?.username) scope.setUser({ id: this.props.currentUser.username })
      Sentry.captureReactException(error, errorInfo, {
        mechanism: {
          handled: true,
          type: 'auto.function.react.error_boundary',
        },
      })
    })
    track(eventTypes.APP_ERROR, {
      boundary,
      error: error.message,
      componentStack: errorInfo.componentStack,
      path,
    })
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Auto-recover when a reset key changes (e.g. the user navigated to a new
    // route) so a transient page-level error doesn't persist across navigation.
    if (this.state.error && !resetKeysAreEqual(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset()
    }
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  handleReload = (): void => {
    window.location.reload()
  }

  // A hard navigation to root rather than a router push: the app tree is in a
  // broken state, so a full document load is the reliable way to recover.
  handleGoHome = (): void => {
    window.location.assign('/')
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    const { fallback, variant = 'fullscreen', boundaryName = 'app' } = this.props
    if (fallback !== undefined) {
      return typeof fallback === 'function' ? fallback({ error, reset: this.reset }) : fallback
    }

    const isInline = variant === 'inline'
    const rootId = `${boundaryName}-error-boundary-fallback`

    return (
      <div id={rootId} role="alert">
        <style>{fallbackStyles(rootId, isInline)}</style>
        <main className="eb-card">
          <div className="eb-mark" aria-hidden="true">
            <img src="/img/company-icon.svg" width={64} height={64} alt="" />
          </div>
          <h1 className="eb-title">Something went wrong</h1>
          {isInline ? (
            <p className="eb-body">An unexpected error occurred. Please try again.</p>
          ) : (
            <p className="eb-body">
              The app ran into an unexpected error and couldn&apos;t continue. Reloading the page usually fixes it, and our team has been
              notified.
            </p>
          )}
          <div className="eb-actions">
            {isInline ? (
              <button
                id={`${boundaryName}-error-boundary-retry-button`}
                className="eb-btn eb-btn-primary"
                type="button"
                onClick={this.reset}
              >
                Try again
              </button>
            ) : (
              <>
                <button
                  id={`${boundaryName}-error-boundary-reload-button`}
                  className="eb-btn eb-btn-primary"
                  type="button"
                  onClick={this.handleReload}
                >
                  Reload page
                </button>
                <button
                  id={`${boundaryName}-error-boundary-home-button`}
                  className="eb-btn eb-btn-secondary"
                  type="button"
                  onClick={this.handleGoHome}
                >
                  Back to home
                </button>
              </>
            )}
          </div>
        </main>
      </div>
    )
  }
}

// Shallow, order-sensitive comparison of the resetKeys arrays (React docs pattern).
function resetKeysAreEqual(a?: unknown[], b?: unknown[]): boolean {
  if (a === b) return true
  if (!a || !b || a.length !== b.length) return false
  return a.every((item, index) => Object.is(item, b[index]))
}

export default ErrorBoundary
